import { ChevronDownIcon } from 'lucide-react';

import { useEffect, useRef, useState } from 'react';

import {
  SUPPORTED_LANGUAGES,
  getEnabledLanguageInfo,
  useTranslation,
} from '@splitfiesta/i18n';
import { Button } from '@splitfiesta/ui';

import { CheckmarkIcon } from '@/components/ui/CheckmarkIcon';

import { getInputClasses } from '@/lib/styles/form';

interface LanguageSelectorProps {
  value: string;
  onChange: (language: string) => void;
  disabled?: boolean;
  'data-testid'?: string;
}

export function LanguageSelector({
  value,
  onChange,
  disabled = false,
  'data-testid': dataTestId,
}: LanguageSelectorProps) {
  const { i18n, t: _t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [_mounted, _setMounted] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const currentLanguage = value || i18n.language;
  const currentLangInfo = SUPPORTED_LANGUAGES.find(
    lang => lang.code === currentLanguage
  );
  const currentName = currentLangInfo?.nativeName || currentLanguage;

  // Mount effect - wrap setState in setTimeout
  useEffect(() => {
    const timer = setTimeout(() => {
      _setMounted(true);
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Only add listener if dropdown is open
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    // Add listener in next tick to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLanguageChange = async (language: string) => {
    setIsOpen(false);
    try {
      // Change UI language
      await i18n.changeLanguage(language);
    } catch (error) {
      // In production, we silently handle the error and still save preference
      if (import.meta.env.DEV) {
        console.error('Failed to change language:', error);
      }
    }
    // Save to database (always call onChange even if language change fails)
    onChange(language);
  };

  const availableLanguages = getEnabledLanguageInfo().map(lang => ({
    code: lang.code,
    name: lang.nativeName,
  }));

  return (
    <div className="relative" ref={menuRef} data-testid={dataTestId}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        rightIcon={
          <ChevronDownIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
        }
        className={`${getInputClasses({ disabled, size: 'sm' })} justify-between cursor-pointer`}
        data-testid={dataTestId ? `${dataTestId}-button` : undefined}
      >
        <div className="flex items-center space-x-2">
          <span>{currentName}</span>
        </div>
      </Button>

      {isOpen && !disabled && (
        <div className="absolute left-0 sm:left-auto sm:right-auto mt-2 w-full sm:w-max bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black/5 dark:ring-gray-700 overflow-hidden z-50">
          <div className="py-1 sm:whitespace-nowrap">
            {availableLanguages.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                data-testid={`language-option-${lang.code}`}
                className="w-full flex items-center pl-2 pr-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#F6F5F3] dark:hover:bg-gray-700/50 transition-colors text-left"
              >
                {currentLanguage === lang.code ? (
                  <CheckmarkIcon className="w-4 h-4 text-orange-500 dark:text-orange-400 shrink-0 mr-1.5" />
                ) : (
                  <div className="w-4 h-4 mr-1.5" />
                )}
                <span>{lang.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
